// visuals.ts — the pictures a quote carries for its customer
// (docs/SERVICE_SWITCHER_PLAN.md §13 item 27; the §9 "renders in the hub" row).
//
// q.visuals began as the coating previews (coatpreview.tsx; §13 item 14): the
// flake floor drawn on the customer's own garage photo. The fence side makes
// pictures too — the 3D studio (viz3d/Studio.tsx) stands the quoted fence in
// the customer's photo, or builds it in a 3D yard — but until now they only
// went to the customer's files as a data URL, which the hub never shows. Now a
// visual carries a `kind`, the studio saves its render onto the estimate the
// hub shows, and the hub shows every kind under its own title. Pure and
// unit-tested (scripts/unit-visuals.mjs); the server's hub scope lets `kind`
// through (hubscope.js VISUAL_FIELDS) and nothing else new.

export type VisualKind = "preview" | "render" | "render3d";
export const VISUAL_KINDS: VisualKind[] = ["preview", "render", "render3d"];
export type Visual = { id: string; at: number; label: string; url: string; kind?: VisualKind; before?: string; w?: number; h?: number; pending?: boolean; [k: string]: any };

const hosted = (u: any) => typeof u === "string" && /^https?:\/\//i.test(u);

/** what a visual is: its own kind, else a coating preview when it carries the blend, else a render */
export function visualKind(v: any): VisualKind {
  if (v && VISUAL_KINDS.includes(v.kind)) return v.kind;
  return v && (v.blend || v.system || v.finish) ? "preview" : "render";
}

/** the hub card's title and its one line of copy, per kind */
export function visualTitle(kind: VisualKind, thing = "fence"): string {
  if (kind === "preview") return "Your floor, previewed";
  if (kind === "render3d") return `Your ${thing} in 3D`;
  return `Your ${thing}, previewed`;
}
export function visualNote(kind: VisualKind, thing = "fence"): string {
  if (kind === "preview") return "Drawn on your own photo. The finished floor's colour depends on the lot and the topcoat; this is a preview, not a sample chip.";
  if (kind === "render3d") return `The ${thing} you're quoted, built in 3D at its real height and spacing — the posts and gates where the layout puts them.`;
  return `The ${thing} you're quoted, standing in your own photo — to scale, lit to match. Slide to compare it with today.`;
}

/** the visuals a customer can see: hosted pictures only, newest first */
export function hostedVisuals(list: any): Visual[] {
  return (Array.isArray(list) ? list : []).filter((v) => v && hosted(v.url)).slice().sort((a, b) => (Number(b.at) || 0) - (Number(a.at) || 0));
}

/** the hub's cards: one per kind present — the photo render first, then the 3D build, then the floor previews */
export function visualGroups(list: any, thing = "fence"): { kind: VisualKind; title: string; note: string; list: Visual[] }[] {
  const all = hostedVisuals(list);
  const order: VisualKind[] = ["render", "render3d", "preview"];
  return order.map((kind) => ({ kind, title: visualTitle(kind, thing), note: visualNote(kind, thing), list: all.filter((v) => visualKind(v) === kind) })).filter((g) => g.list.length > 0);
}

/** the studio's render as a visual: a photo render keeps the customer's photo as the "before"; a 3D yard has none */
export function renderVisual(o: { mode: "photo" | "yard"; label?: string; url: string; before?: string | null; w?: number; h?: number; pending?: boolean; now?: number; id?: string }): Visual {
  const at = o.now || Date.now();
  const v: Visual = { id: o.id || "vz" + at.toString(36), at, kind: o.mode === "yard" ? "render3d" : "render", label: String(o.label || "").trim() || "Your fence", url: String(o.url || "") };
  if (o.mode !== "yard" && typeof o.before === "string" && o.before) v.before = o.before;
  if (Number(o.w) > 0) v.w = Number(o.w);
  if (Number(o.h) > 0) v.h = Number(o.h);
  if (o.pending) v.pending = true;
  return v;
}

/** the customer's newest estimate that can carry a picture — the one the hub shows; a lost one never */
export function quoteForVisual(quotes: any, customer: any): any | null {
  if (!customer) return null;
  const name = String(customer.name || "").trim().toLowerCase();
  const mine = (Array.isArray(quotes) ? quotes : []).filter((q) => q && q.status !== "lost" && ((customer.id && q.customerId === customer.id) || (!q.customerId && name && String(q.customer || "").trim().toLowerCase() === name)));
  if (!mine.length) return null;
  const stamp = (q: any) => Number(q.updatedAt) || Number(q.createdAt) || 0;
  return mine.slice().sort((a, b) => stamp(b) - stamp(a))[0];
}

/** the quote's visuals with this one added — or replaced, by id */
export function withVisual(q: any, v: Visual): Visual[] {
  const list: Visual[] = Array.isArray(q && q.visuals) ? q.visuals.filter((x: any) => x && x.id !== v.id) : [];
  return [...list, v];
}
